// src/components/NotificationSettings.js
import React, { useState, useEffect } from 'react';
import { soundService } from '../services/soundService';

const NotificationSettings = ({ onClose }) => {
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [vibrationEnabled, setVibrationEnabled] = useState(true);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    // Load current preferences
    setSoundEnabled(soundService.getSoundPreference());
    setVibrationEnabled(soundService.getVibrationPreference());
  }, []);

  const handleSoundToggle = () => {
    const newValue = !soundEnabled;
    setSoundEnabled(newValue);
    
    // Test sound if enabled
    if (newValue) {
      soundService.playSound('bell-loud.mp3', 0.5);
    }
  };

  const handleVibrationToggle = () => {
    const newValue = !vibrationEnabled;
    setVibrationEnabled(newValue);
    
    // Test vibration if enabled
    if (newValue) {
      soundService.vibrate([200, 100, 200]);
    }
  };

  const handleSave = () => {
    soundService.setSoundPreference(soundEnabled);
    soundService.setVibrationPreference(vibrationEnabled);
    
    setSaved(true);
    setTimeout(() => {
      setSaved(false);
      onClose();
    }, 1000);
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      animation: 'fadeIn 0.3s ease'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '15px',
        padding: '30px',
        maxWidth: '450px',
        width: '90%',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        animation: 'slideIn 0.3s ease'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '25px',
          paddingBottom: '15px',
          borderBottom: '2px solid #f0f0f0'
        }}>
          <h3 style={{ margin: 0, fontSize: '1.5rem', color: '#333', fontWeight: '700' }}>
            🔔 Notification Settings
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              fontSize: '1.8rem',
              cursor: 'pointer',
              color: '#999',
              transition: 'color 0.2s ease'
            }}
            onMouseOver={(e) => e.target.style.color = '#333'}
            onMouseOut={(e) => e.target.style.color = '#999'}
          >
            ×
          </button>
        </div>

        {/* Sound Toggle */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '20px',
          background: '#f8f9fa',
          borderRadius: '10px',
          marginBottom: '15px',
          border: '2px solid ' + (soundEnabled ? '#28a745' : '#e9ecef'),
          transition: 'all 0.3s ease'
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ 
              fontSize: '1.1rem', 
              fontWeight: '600', 
              color: '#333', 
              marginBottom: '5px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              {soundEnabled ? '🔊' : '🔇'} Sound Notifications
            </div>
            <div style={{ fontSize: '0.9rem', color: '#666' }}>
              Play alert sound for new orders
            </div>
          </div>
          
          {/* Toggle Switch */}
          <button
            onClick={handleSoundToggle}
            style={{
              width: '60px',
              height: '32px',
              borderRadius: '16px',
              border: 'none',
              background: soundEnabled ? '#28a745' : '#ccc',
              cursor: 'pointer',
              position: 'relative',
              transition: 'background 0.3s ease',
              flexShrink: 0,
              marginLeft: '15px'
            }}
          >
            <div style={{
              width: '26px',
              height: '26px',
              borderRadius: '50%',
              background: 'white',
              position: 'absolute',
              top: '3px',
              left: soundEnabled ? '31px' : '3px',
              transition: 'left 0.3s ease',
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
            }} />
          </button>
        </div>

        {/* Vibration Toggle */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '20px',
          background: '#f8f9fa',
          borderRadius: '10px',
          marginBottom: '25px',
          border: '2px solid ' + (vibrationEnabled ? '#28a745' : '#e9ecef'),
          transition: 'all 0.3s ease'
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ 
              fontSize: '1.1rem', 
              fontWeight: '600', 
              color: '#333', 
              marginBottom: '5px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              📳 Vibration
            </div>
            <div style={{ fontSize: '0.9rem', color: '#666' }}>
              Vibrate device for new orders
            </div>
          </div>
          
          {/* Toggle Switch */}
          <button
            onClick={handleVibrationToggle}
            style={{
              width: '60px',
              height: '32px',
              borderRadius: '16px',
              border: 'none',
              background: vibrationEnabled ? '#28a745' : '#ccc',
              cursor: 'pointer',
              position: 'relative',
              transition: 'background 0.3s ease',
              flexShrink: 0,
              marginLeft: '15px'
            }}
          >
            <div style={{
              width: '26px',
              height: '26px',
              borderRadius: '50%',
              background: 'white',
              position: 'absolute',
              top: '3px',
              left: vibrationEnabled ? '31px' : '3px',
              transition: 'left 0.3s ease',
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
            }} />
          </button>
        </div>

        {/* Info Box */}
        <div style={{
          background: '#fff3cd',
          border: '1px solid #ffc107',
          borderRadius: '8px',
          padding: '12px',
          marginBottom: '20px',
          fontSize: '0.85rem',
          color: '#856404'
        }}>
          💡 <strong>Tip:</strong> Keep sound and vibration enabled to never miss an order!
        </div>

        {/* Save Button */}
        <button
          onClick={handleSave}
          disabled={saved}
          style={{
            width: '100%',
            background: saved ? '#28a745' : 'linear-gradient(135deg, #FF4444, #FF6B00)',
            color: 'white',
            border: 'none',
            borderRadius: '10px',
            padding: '15px',
            fontSize: '1.1rem',
            fontWeight: '600',
            cursor: saved ? 'not-allowed' : 'pointer',
            transition: 'all 0.3s ease',
            boxShadow: '0 4px 15px rgba(255, 68, 68, 0.3)'
          }}
        >
          {saved ? '✓ Settings Saved!' : 'Save Settings'}
        </button>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slideIn {
          from { 
            transform: translateY(-30px);
            opacity: 0;
          }
          to { 
            transform: translateY(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default NotificationSettings;